import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Users, Film, CalendarClock, AlertTriangle, Loader2, ArrowRight } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone = "emerald", testid }) => {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    stone: "bg-stone-100 text-stone-700",
  };
  return (
    <div data-testid={testid} className="rounded-lg border border-stone-200 bg-white p-6 transition-all hover:shadow-md hover:-translate-y-0.5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-4 font-heading text-3xl font-bold text-stone-900">{value}</p>
      <p className="text-sm text-stone-500 mt-1">{label}</p>
    </div>
  );
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/dashboard/admin");
      setData(res.data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Overview</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mt-1">Admin Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
        <Stat icon={Users} label="Total MRs" value={data.total_mrs} tone="emerald" testid="stat-total-mrs" />
        <Stat icon={Film} label="Videos generated" value={data.total_videos} tone="blue" testid="stat-total-videos" />
        <Stat icon={CalendarClock} label="Generated today" value={data.videos_today} tone="stone" testid="stat-videos-today" />
        <Stat icon={Loader2} label="Processing" value={data.processing} tone="amber" testid="stat-processing" />
        <Stat icon={AlertTriangle} label="Failed renders" value={data.failed} tone="red" testid="stat-failed" />
      </div>

      <div className="rounded-lg border border-stone-200 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h3 className="font-heading text-lg font-semibold text-stone-900">Recent uploads</h3>
          <Link to="/admin/videos" className="text-sm font-medium text-emerald-700 inline-flex items-center gap-1 hover:gap-1.5 transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {data.recent.length === 0 ? (
          <p className="px-6 py-10 text-center text-stone-500">No videos generated yet.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {data.recent.map((v) => (
              <div key={v.id} className="flex items-center justify-between px-6 py-4" data-testid={`recent-row-${v.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-stone-900 truncate">{v.doctor_name}</p>
                  <p className="text-xs text-stone-500">{v.mr_name} · {v.region || "—"} · {v.place || v.doctor_city}</p>
                </div>
                <StatusBadge status={v.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
